import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time errors from the React tree and renders a terminal
 * fallback page. Per V1.0_PLAN polish — prevents the operator from seeing
 * a blank white screen when a component throws.
 *
 * Class component is required: React 19 still has no hook equivalent for
 * `componentDidCatch` / `getDerivedStateFromError`.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // V1.1+: ship to Sentry / Datadog. For V1.0 console.error is enough —
    // operators have devtools open during the only deployments today.
     
    console.error('[ErrorBoundary] caught render error:', error, info);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="error-boundary-pane">
          <div className="error-boundary-card" role="alert">
            <p className="not-found-eyebrow">Something went wrong</p>
            <h1>The operator console hit a render error</h1>
            <p>
              The page failed to render. This is a bug — please refresh, and
              if it keeps happening, drop us a note with what you were doing.
            </p>
            <pre className="error-boundary-detail">
              {this.state.error.name}: {this.state.error.message}
            </pre>
            <div className="error-boundary-actions">
              <button
                type="button"
                className="login-form-submit"
                onClick={this.handleReload}
              >
                Reload page
              </button>
              <a href="mailto:support@tizok.ai" className="error-boundary-mailto">
                Email support
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
